import { useState, useEffect, useRef, useCallback } from 'react';
import { CONFIG } from '@/config';
import type { PlaylistItem } from '@/types';
import { recordPlayback } from './proof-of-play';
import { trackVideoStart } from '@/core/perf';
import SensarIdle from './SensarIdle';

interface PlaybackEngineProps {
  playlist: PlaylistItem[];
  boardId: string;
  boardCode?: string | null;
  /** Index to resume from when the playlist is rebuilt — see PlayerScreen survivedIndex. */
  initialIndex?: number;
  /** Fires whenever the visible item changes; PlayerScreen tracks it for continuity. */
  onCurrentChange?: (mediaId: string | null) => void;
  onPresenceChange?: (present: boolean) => void;
}

export default function PlaybackEngine({
  playlist,
  boardId,
  boardCode,
  initialIndex = 0,
  onCurrentChange,
}: PlaybackEngineProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  // Monotonic counter bumped on EVERY advance — included in the <video>/<img>
  // key so React remounts the element even when currentIndex didn't change
  // (single-item playlist: (0+1)%1 = 0). Without this a 1-item playlist
  // froze after the first play because the same DOM node was reused, the
  // video element was already at its end, and autoplay wouldn't restart.
  const [cycleCount, setCycleCount] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const startTimeRef = useRef<number>(Date.now());
  // Tracks paused-time for accurate proof-of-play duration on videos.
  const accumulatedPlayMsRef = useRef<number>(0);
  const lastPlayResumeRef = useRef<number>(Date.now());
  // Race guard: prevents the image timer + video.onEnded from both firing
  // advanceToNext for the same item (which would skip two slots and report
  // a near-zero duration for the second).
  const advancingRef = useRef<boolean>(false);

  const current = playlist[currentIndex] || null;

  // Notify parent of the currently-playing media id (for survivedIndex continuity).
  useEffect(() => {
    onCurrentChange?.(current?.media.id ?? null);
  }, [current, onCurrentChange]);

  const advanceToNext = useCallback(() => {
    if (playlist.length === 0) return;
    if (advancingRef.current) return;
    advancingRef.current = true;

    // Record proof of play for the item that just finished. Use video's
    // own `currentTime` when possible — it excludes pause/buffering periods
    // that wall-clock would over-count.
    if (current) {
      let durationSec: number;
      if (current.media.type === 'video' && videoRef.current) {
        durationSec = Math.round(videoRef.current.currentTime);
      } else {
        // For images, account for any visibility-pause via accumulator.
        const liveMs = Date.now() - lastPlayResumeRef.current;
        durationSec = Math.round((accumulatedPlayMsRef.current + liveMs) / 1000);
      }
      if (durationSec > 0) {
        // PlanMedia has no `name` — log the id (server resolves it from MediaItem).
        recordPlayback(current.campaignId, boardId, current.media.id, durationSec);
      }
    }

    // Transition
    setFadeIn(false);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % playlist.length);
      setCycleCount((c) => c + 1);
      startTimeRef.current = Date.now();
      lastPlayResumeRef.current = Date.now();
      accumulatedPlayMsRef.current = 0;
      setFadeIn(true);
      advancingRef.current = false;
    }, CONFIG.PLAYBACK_TRANSITION_MS);
  }, [playlist, current, boardId]);

  // Reset index when playlist changes — but honor the parent-supplied
  // initialIndex so a plan rebuild resumes from where we were rather than
  // jumping to slot 0 each cycle.
  useEffect(() => {
    setCurrentIndex(Math.min(initialIndex, Math.max(0, playlist.length - 1)));
    startTimeRef.current = Date.now();
    lastPlayResumeRef.current = Date.now();
    accumulatedPlayMsRef.current = 0;
    advancingRef.current = false;
  }, [playlist, initialIndex]);

  // Auto-advance for images (videos advance on 'ended' event)
  useEffect(() => {
    if (!current || current.media.type === 'video') return;

    const duration = current.media.durationMs
      ?? CONFIG.DEFAULT_IMAGE_DURATION_MS;

    const timer = setTimeout(advanceToNext, duration);
    return () => clearTimeout(timer);
  }, [current, currentIndex, advanceToNext]);

  // Pause/resume bookkeeping for accurate image durations (e.g. when
  // Capacitor sends the app to background).
  useEffect(() => {
    if (!current) return;
    const onVisibilityChange = () => {
      if (document.hidden) {
        accumulatedPlayMsRef.current += Date.now() - lastPlayResumeRef.current;
      } else {
        lastPlayResumeRef.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [current, currentIndex]);

  // Empty playlist (no cached media yet, no plan, or paused fleet) →
  // show idle. The scheduler guarantees every item in the playlist has
  // a usable localPath, so we only need to check `current` itself.
  if (!current) {
    return <SensarIdle boardCode={boardCode ?? undefined} />;
  }

  return (
    <div style={styles.container}>
      <div
        style={{
          ...styles.mediaWrapper,
          opacity: fadeIn ? 1 : 0,
          transition: `opacity ${CONFIG.PLAYBACK_TRANSITION_MS}ms ease-in-out`,
        }}
      >
        {current.media.type === 'video' ? (
          <video
            ref={(el) => {
              videoRef.current = el;
              // Instrument start latency. trackVideoStart() de-dupes
              // via WeakMap so repeated refs on the same element are
              // safe.
              if (el && current.localPath) {
                trackVideoStart(el, current.localPath);
              }
            }}
            key={current.media.id + '-' + cycleCount}
            src={current.localPath}
            autoPlay
            muted
            playsInline
            onEnded={advanceToNext}
            onError={advanceToNext}
            style={styles.media}
          />
        ) : (
          <img
            key={current.media.id + '-' + cycleCount}
            src={current.localPath}
            alt={current.media.id}
            style={styles.media}
          />
        )}
      </div>

      {/* Device watermark */}
      {boardCode && (
        <div style={styles.watermark}>{boardCode}</div>
      )}

      {/* Debug overlay — hidden in production */}
      {import.meta.env.DEV && (
        <div style={styles.debug}>
          <span>{current.campaignId.slice(0, 8)}</span>
          <span>{current.media.id.slice(0, 8)}</span>
          <span>P{current.priority} | {currentIndex + 1}/{playlist.length}</span>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    backgroundColor: '#000',
    overflow: 'hidden',
  },
  mediaWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  media: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  watermark: {
    position: 'fixed',
    bottom: 6,
    right: 8,
    fontSize: 9,
    fontFamily: 'monospace',
    color: 'rgba(255,255,255,0.15)',
    zIndex: 1,
    pointerEvents: 'none',
    userSelect: 'none',
  },
  debug: {
    position: 'fixed',
    bottom: 8,
    left: 8,
    display: 'flex',
    gap: 12,
    padding: '4px 10px',
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: '#0f0',
    fontSize: 11,
    fontFamily: 'monospace',
    zIndex: 999,
  },
};
